import Link from "next/link";

import { Anchor } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";

import ContentCard from "@/components/ContentCard/";
import HorizontalScrollContainer from "@/components/HorizontalScrollContainer";
import { omitSkillsFromResults } from "../HomepageDefaultRow/HomepageDefaultRow";

import { ContentRowProps } from "../ContentRow.interface";
import {
  contentRowContainerCss,
  contentRowDescriptionCss,
  contentRowHeadingCss,
  contentRowLabelCss,
  contentListCss,
  contentScrollableListCss,
  linkContainerCss,
  listItemCss,
} from "./HomepageImportantRow.styles";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const FIXED_CONTAINER_MAX_ITEMS = 3;
const SCROLL_CONTAINER_MAX_ITEMS = 8;

const HomepageImportantRow = ({
  dataQaSelector = "",
  description,
  label,
  children: rowChildren,
  title,
  url,
}: ContentRowProps) => {
  const [removeReadingSkillChips] = useGetUserExperimentSegment(
    "remove_reading_skills_from_cards",
  );

  const isExperimentRemoveReadingSkillChipsFromSearch =
    removeReadingSkillChips === "on";

  if (rowChildren.length === 0) {
    return null;
  }

  const sanitizedResults = isExperimentRemoveReadingSkillChipsFromSearch
    ? omitSkillsFromResults(rowChildren)
    : rowChildren;

  if (rowChildren.length <= FIXED_CONTAINER_MAX_ITEMS) {
    return (
      <section css={contentRowContainerCss}>
        {label ? <div css={contentRowLabelCss}>{label}</div> : null}
        <h2 css={contentRowHeadingCss}>{title}</h2>
        <p css={contentRowDescriptionCss}>{description}</p>

        <ul css={contentListCss}>
          {sanitizedResults
            .slice(0, FIXED_CONTAINER_MAX_ITEMS)
            .map((cardViewmodel, index) => (
              <li css={listItemCss} key={index}>
                <ContentCard {...cardViewmodel} />
              </li>
            ))}
        </ul>
      </section>
    );
  }

  return (
    <section css={contentRowContainerCss}>
      {label ? <div css={contentRowLabelCss}>{label}</div> : null}
      <h2 css={contentRowHeadingCss}>{title}</h2>
      <p css={contentRowDescriptionCss}>{description}</p>
      {url ? (
        <AnchorWithTrackEvent
          href={url}
          dataProps={{ "data-qa-selector": "view-all-content-row-link" }}
          __cssFor={{ root: linkContainerCss }}
          objectType={objectTypes.content}
          actionName={actionNames.navigate}
          streamProperties={{
            description: "Navigate to view all content in a content row",
          }}
          __renderCustomLink={({ children, href, ...anchorProps }) => {
            return (
              <Link href={href as URL} passHref legacyBehavior>
                <a {...anchorProps}>{children}</a>
              </Link>
            );
          }}
        >
          View All
        </AnchorWithTrackEvent>
      ) : null}

      <HorizontalScrollContainer>
        <ul css={contentScrollableListCss}>
          {sanitizedResults
            .slice(0, SCROLL_CONTAINER_MAX_ITEMS)
            .map((cardViewmodel, index) => (
              <li css={listItemCss} key={index}>
                <ContentCard
                  {...cardViewmodel}
                  dataQaSelector={dataQaSelector}
                />
              </li>
            ))}
        </ul>
      </HorizontalScrollContainer>
    </section>
  );
};

HomepageImportantRow.displayName = "HomepageImportantRow";
export default HomepageImportantRow;
